/**
 * Zustand store для управления конфигурацией цен
 *
 * Загружает цены с бэкенда и кэширует их локально.
 * Поддерживает ETag для эффективного кэширования.
 * Fallback на статические цены если бэкенд недоступен.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Типы для pricing конфига
export interface VideoPricing {
  [provider: string]: {
    [model: string]: {
      [quality: string]: {
        [duration: string]: number;
      };
    } | {
      perSecond: number;
    } | {
      // Для моделей с аудио (Seedance 1.5 Pro, Kling 2.6, Veo)
      [audioKey: string]: {
        [quality: string]: {
          [duration: string]: number;
        };
      } | {
        [duration: string]: number; // Для Kling 2.6 и Veo (без quality)
      };
    };
  } | {
    [quality: string]: number;
  };
}

export interface ImageModelData {
  displayName: string;
  sortOrder: number;
  pricing: Record<string, number>;
  hidden?: boolean;
  batchPricing?: boolean;
  perInputImageTokens?: number;
}

export type ImageModelsMap = Record<string, ImageModelData>;

export interface SubscriptionTierData {
  id: string;
  name: string;
  displayName: string;
  priceMonthly: number;
  priceYearly: number;
  tokensMonthly: number;
  tokensYearly: number;
  isWelcomeTokens?: boolean;
}

export interface CurrencyData {
  tokensPerRuble: number;
  rublesPerToken: number;
}

export interface TokenPackage {
  tokens: number;
  price: number;
  bonus?: number;
}

export type TierAccessMap = Record<string, Record<string, string[]>>;

export interface ChatModelConfig {
  value: string;
  text: string;
  tagline?: string;
  supportsVision: boolean;
  capabilities: Record<string, boolean>;
  requiredTier: 'free' | 'basic' | 'pro' | 'max';
  pricing: { input: number; output: number };
  badges?: ('top' | 'highReasoning' | 'fast' | 'transparentBg')[];
}

export interface ChatProviderConfig {
  displayName: string;
  description: string;
  sortOrder: number;
  models: ChatModelConfig[];
}

export type ChatModelsMap = Record<string, ChatProviderConfig>;

export interface VideoModelParams {
  quality: string[];
  duration: string[];
  ratio: string[];
  hasRatio: boolean;
  supportsFrames: boolean;
  frameTypes: ('start' | 'end')[];
  supportsMotions?: boolean;
  requiresStartFrame?: boolean;
  supportsRemix?: boolean;
  supportsAudio?: boolean;
  audioOnly?: boolean;
  supportsVideoEdit?: boolean;
  supportsVideoExtend?: boolean;
  requiresReferenceVideo?: boolean;
  referenceVideoDuration?: { min: number; max: number };
  supportsReferenceMode?: boolean;
  supportsMultiShot?: boolean;
  supportsElements?: boolean;
  supportsImageReferences?: boolean;
  supportsVideoReferences?: boolean;
  supportsAudioReferences?: boolean;
  hasDurationSlider?: boolean;
}

export interface VideoModelConfig {
  id: string;
  name: string;
  available: boolean;
  sortOrder?: number;
  description?: string;
  requiredTier?: 'free' | 'basic' | 'pro' | 'max';
  badge?: 'top' | 'fast' | 'highReasoning';
  tokenPrice?: number;
  parameters: VideoModelParams;
}

export interface VideoProviderConfig {
  name: string;
  models: VideoModelConfig[];
}

export type VideoModelsMap = Record<string, VideoProviderConfig>;

export interface PricingConfig {
  video: VideoPricing;
  videoModels: VideoModelsMap;
  imageModels: ImageModelsMap;
  subscriptions?: Record<string, SubscriptionTierData>;
  chat?: Record<string, any>;
  features?: Record<string, number>;
  currency?: CurrencyData;
  tokenPackages?: TokenPackage[];
  version: string;
}

interface PricingState {
  // Данные
  videoPricing: VideoPricing | null;
  videoModels: VideoModelsMap | null;
  imageModels: ImageModelsMap | null;
  subscriptions: Record<string, SubscriptionTierData> | null;
  chatPricing: Record<string, any> | null;
  chatModels: ChatModelsMap | null;
  featurePricing: Record<string, number> | null;
  currency: CurrencyData | null;
  tokenPackages: TokenPackage[] | null;
  tierAccess: TierAccessMap | null;
  version: string | null;

  // Состояние загрузки
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  lastFetchTime: number;

  // Действия
  loadPricing: (forceRefresh?: boolean) => Promise<void>;
  clearCache: () => void;
  checkVersionAndRefresh: () => Promise<void>;

  // Геттеры для использования в расчётах
  getVideoPricing: () => VideoPricing | null;
}

// Время кэширования (1 час — версионная проверка при фокусе окна обновит раньше если нужно)
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 час

// Минимальный интервал между проверками версии (5 минут)
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Ключ для хранения ETag
const ETAG_STORAGE_KEY = 'pricing-etag';

// Время последней проверки версии (модульная переменная, не в persist)
let _lastVersionCheckTime = 0;

export const usePricingStore = create<PricingState>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      videoPricing: null,
      videoModels: null,
      imageModels: null,
      subscriptions: null,
      chatPricing: null,
      chatModels: null,
      featurePricing: null,
      currency: null,
      tokenPackages: null,
      tierAccess: null,
      version: null,
      isLoading: false,
      isLoaded: false,
      error: null,
      lastFetchTime: 0,

      /**
       * Загрузить конфигурацию цен с бэкенда
       * Поддерживает условные запросы с ETag
       */
      loadPricing: async (forceRefresh = false) => {
        const state = get();

        // Проверяем, не выполняется ли уже запрос
        if (state.isLoading) {
          return;
        }

        // Проверяем кэш (если не форсированное обновление)
        const now = Date.now();
        if (!forceRefresh && state.isLoaded && state.videoPricing && state.videoModels && state.imageModels) {
          const timeSinceLastFetch = now - state.lastFetchTime;
          if (timeSinceLastFetch < CACHE_DURATION_MS) {
            return;
          }
        }

        set({ isLoading: true, error: null });

        try {
          // Получаем сохранённый ETag для условного запроса
          const savedETag = localStorage.getItem(ETAG_STORAGE_KEY);

          const headers: HeadersInit = {
            'Content-Type': 'application/json'
          };

          // Добавляем If-None-Match если есть ETag и данные уже загружены
          if (savedETag && state.isLoaded && !forceRefresh) {
            headers['If-None-Match'] = savedETag;
          }

          const response = await fetch('/api/pricing-config', {
            headers,
            cache: forceRefresh ? 'reload' : 'default'
          });

          // 304 Not Modified - данные не изменились
          if (response.status === 304) {
            console.log('📦 Pricing config: 304 Not Modified (using cache)');
            set({
              isLoading: false,
              lastFetchTime: now
            });
            return;
          }

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();

          if (!data.success || !data.data) {
            throw new Error('Invalid response from pricing API');
          }

          // Сохраняем ETag для следующих запросов
          const newETag = response.headers.get('ETag');
          if (newETag) {
            localStorage.setItem(ETAG_STORAGE_KEY, newETag);
          }

          console.log('✅ Pricing config loaded from server');

          set({
            videoPricing: data.data.video,
            videoModels: data.data.videoModels || null,
            imageModels: data.data.imageModels,
            subscriptions: data.data.subscriptions || null,
            chatPricing: data.data.chat || null,
            chatModels: data.data.chatModels || null,
            featurePricing: data.data.features || null,
            currency: data.data.currency || null,
            tokenPackages: data.data.tokenPackages || null,
            tierAccess: data.data.tierAccess || null,
            version: data.version || newETag || String(now),
            isLoading: false,
            isLoaded: true,
            error: null,
            lastFetchTime: now
          });

        } catch (error: any) {
          console.error('❌ Failed to load pricing config:', error);

          // Если данные уже загружены - используем их (stale-while-revalidate)
          if (state.isLoaded && state.videoPricing && state.imageModels) {
            console.warn('⚠️ Using stale pricing config from cache');
            set({
              isLoading: false,
              lastFetchTime: now
            });
            return;
          }

          set({
            isLoading: false,
            error: error.message || 'Failed to load pricing config'
          });
        }
      },

      /**
       * Очистить кэш (для отладки или принудительного обновления)
       */
      clearCache: () => {
        localStorage.removeItem(ETAG_STORAGE_KEY);
        set({
          videoPricing: null,
          videoModels: null,
          imageModels: null,
          subscriptions: null,
          chatPricing: null,
          chatModels: null,
          featurePricing: null,
          currency: null,
          tokenPackages: null,
          tierAccess: null,
          version: null,
          isLoaded: false,
          lastFetchTime: 0
        });
      },

      /**
       * Проверить версию на сервере и обновить данные если изменилась.
       * Лёгкий запрос (~50 байт), вызывается при фокусе окна.
       * Не чаще раза в VERSION_CHECK_INTERVAL_MS.
       */
      checkVersionAndRefresh: async () => {
        const state = get();
        if (state.isLoading || !state.isLoaded) return;

        // Не проверяем слишком часто
        const now = Date.now();
        if (now - _lastVersionCheckTime < VERSION_CHECK_INTERVAL_MS) return;
        _lastVersionCheckTime = now;

        try {
          const response = await fetch('/api/pricing-config/version', { cache: 'no-cache' });
          if (!response.ok) return;

          const data = await response.json();
          const serverVersion = data.version;

          // Если версия изменилась — загружаем свежие данные
          if (serverVersion && serverVersion !== state.version) {
            console.log('🔄 Pricing version changed, refreshing...', { old: state.version, new: serverVersion });
            await get().loadPricing(true);
          }
        } catch {
          // Тихо игнорируем ошибки версионной проверки
        }
      },

      /**
       * Получить цены на видео (для использования в расчётах)
       */
      getVideoPricing: () => {
        return get().videoPricing;
      },

      // getImagePricing removed — use getImageModelsSync() instead
    }),
    {
      name: 'pricing-storage', // Ключ в localStorage
      partialize: (state) => ({
        // Сохраняем только данные, не состояние загрузки
        videoPricing: state.videoPricing,
        videoModels: state.videoModels,
        imageModels: state.imageModels,
        subscriptions: state.subscriptions,
        chatPricing: state.chatPricing,
        chatModels: state.chatModels,
        featurePricing: state.featurePricing,
        currency: state.currency,
        tokenPackages: state.tokenPackages,
        tierAccess: state.tierAccess,
        version: state.version,
        lastFetchTime: state.lastFetchTime,
        isLoaded: state.isLoaded
      })
    }
  )
);

// ===== Селекторы для удобства =====

/**
 * Хук для получения цен на видео
 */
export const useVideoPricing = () => {
  return usePricingStore(state => state.videoPricing);
};

/**
 * Хук для получения image models (displayName + pricing)
 */
export const useImageModels = () => {
  return usePricingStore(state => state.imageModels);
};

/**
 * Хук для получения подписок
 */
export const useSubscriptions = () => {
  return usePricingStore(state => state.subscriptions);
};

/**
 * Хук для получения данных о валюте
 */
export const useCurrency = () => {
  return usePricingStore(state => state.currency);
};

/**
 * Хук для получения пакетов токенов
 */
export const useTokenPackages = () => {
  return usePricingStore(state => state.tokenPackages);
};

/**
 * Хук для проверки загружены ли цены
 */
export const useIsPricingLoaded = () => {
  return usePricingStore(state => state.isLoaded);
};

/**
 * Хук для получения состояния загрузки
 */
export const usePricingLoading = () => {
  return usePricingStore(state => ({
    isLoading: state.isLoading,
    error: state.error
  }));
};

// ===== Функции для использования вне React компонентов =====

/**
 * Получить цены на видео (синхронно из store)
 * Для использования в функциях расчёта цен
 */
export function getVideoPricingSync(): VideoPricing | null {
  return usePricingStore.getState().videoPricing;
}

/**
 * Получить image models (синхронно из store)
 * Единый источник: displayName + pricing
 */
export function getImageModelsSync(): ImageModelsMap | null {
  return usePricingStore.getState().imageModels;
}

/**
 * Получить подписки (синхронно из store)
 */
export function getSubscriptionsSync(): Record<string, SubscriptionTierData> | null {
  return usePricingStore.getState().subscriptions;
}

/**
 * Получить данные о валюте (синхронно из store)
 */
export function getCurrencySync(): CurrencyData | null {
  return usePricingStore.getState().currency;
}

/**
 * Получить пакеты токенов (синхронно из store)
 */
export function getTokenPackagesSync(): TokenPackage[] | null {
  return usePricingStore.getState().tokenPackages;
}

/**
 * Получить tier access map (синхронно из store)
 */
export function getTierAccessSync(): TierAccessMap | null {
  return usePricingStore.getState().tierAccess;
}

/**
 * Хук для получения tier access
 */
export const useTierAccess = () => {
  return usePricingStore(state => state.tierAccess);
};

/**
 * Хук для получения chatModels (полный конфиг из chat-registry)
 */
export const useChatModels = () => {
  return usePricingStore(state => state.chatModels);
};

/**
 * Хук для получения videoModels (полный конфиг из video-registry)
 */
export const useVideoModels = () => {
  return usePricingStore(state => state.videoModels);
};

/**
 * Получить videoModels (синхронно из store)
 */
export function getVideoModelsSync(): VideoModelsMap | null {
  return usePricingStore.getState().videoModels;
}

/**
 * Получить chatModels (синхронно из store)
 */
export function getChatModelsSync(): ChatModelsMap | null {
  return usePricingStore.getState().chatModels;
}

/**
 * Проверить загружены ли цены (синхронно)
 */
export function isPricingLoadedSync(): boolean {
  return usePricingStore.getState().isLoaded;
}

// ===== Автоматическая проверка версии при фокусе окна =====

let _versionCheckRegistered = false;

/**
 * Инициализировать автоматическую проверку версии при фокусе окна.
 * Вызывается один раз при монтировании App.
 * Проверяет версию цен на сервере не чаще раза в 5 минут.
 * Если версия изменилась (админ обновил цены) — автоматически подгружает свежие данные.
 */
export function initPricingVersionCheck(): () => void {
  if (_versionCheckRegistered) return () => {};
  _versionCheckRegistered = true;

  const handleFocus = () => {
    usePricingStore.getState().checkVersionAndRefresh();
  };

  window.addEventListener('focus', handleFocus);

  return () => {
    window.removeEventListener('focus', handleFocus);
    _versionCheckRegistered = false;
  };
}
